/**
 * "Couldn't draft this quote" — what Closeout shows instead of a silent $0
 * quote when the job has a failed device and the draft has no lines
 * (2026-10-28, FWISD Meadowbrook Elementary, Scope Out #102678833: a failed
 * 1-1/4" 909 reached the office as a blank $0 quote with only "No lines yet").
 *
 * Says why in one line, then two one-tap ways forward: draft it from the
 * test (the same rebuild as "Rebuild this quote", nothing to lose on an empty
 * quote), or add the lines by hand.
 */
import { Loader2, RefreshCw, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InfoButton, type InfoTopic } from "@/components/info-button";

export const EMPTY_QUOTE_HELP: InfoTopic = {
    title: "Why is this quote empty?",
    lines: [
        "A device failed on this job, but this quote has no lines, so it would go out at $0.",
        "Draft it from the test builds the lines from what the tech recorded: the device, his pick (repair or replace) and the re-test.",
        "Add lines by hand opens the quote so you can pick from the pricebook yourself.",
        "Nothing goes to the customer until Corbin approves it.",
    ],
};

export function EmptyQuoteBanner({
    why,
    drafting,
    onDraft,
    onAddByHand,
}: {
    why: string;
    drafting: boolean;
    onDraft: () => void;
    onAddByHand: () => void;
}) {
    return (
        <div
            data-testid="closeout-empty-quote"
            className="rounded-lg border-l-[3px] border-amber-500 bg-amber-50/80 p-3 dark:bg-amber-900/20"
        >
            <div className="flex items-start justify-between gap-2">
                <div>
                    <div className="text-[13px] font-bold text-amber-900 dark:text-amber-200">
                        Couldn&apos;t draft this quote — it&apos;s empty
                    </div>
                    <p className="mt-0.5 text-[12.5px] text-foreground/80">{why}</p>
                </div>
                <InfoButton topic={EMPTY_QUOTE_HELP} />
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
                <Button size="sm" className="h-8" disabled={drafting} onClick={onDraft}>
                    {drafting ? (
                        <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                    ) : (
                        <RefreshCw className="mr-1 h-3.5 w-3.5" />
                    )}
                    Draft it from the test
                </Button>
                <Button size="sm" variant="outline" className="h-8" onClick={onAddByHand}>
                    <Pencil className="mr-1 h-3.5 w-3.5" />
                    Add lines by hand
                </Button>
            </div>
        </div>
    );
}
